import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Popconfirm, Spin, Switch, Table, Tag } from "antd";
import { toast } from "react-toastify";
import PermissionGate from "../commoncomponent/PermissionGate";
import {
  getMaintenanceConfig,
  upsertMaintenanceConfig,
  getMaintenanceHistory,
} from "../../../service/api";

const PLATFORMS = [
  { key: "android", label: "Android" },
  { key: "ios", label: "iOS" },
];

const CHANGE_TAG = {
  TURNED_ON: { color: "red", label: "Turned ON" },
  TURNED_OFF: { color: "green", label: "Turned OFF" },
  UPDATED: { color: "default", label: "Updated" },
};

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : "—");

const HISTORY_COLUMNS = [
  { title: "When", dataIndex: "createdAt", render: fmtDateTime },
  {
    title: "Platform",
    dataIndex: "platform",
    render: (v) => <Tag>{v === "ios" ? "iOS" : "Android"}</Tag>,
  },
  {
    title: "Change",
    dataIndex: "changeType",
    render: (v) => {
      const t = CHANGE_TAG[v] || CHANGE_TAG.UPDATED;
      return <Tag color={t.color}>{t.label}</Tag>;
    },
  },
  { title: "ETA", dataIndex: "estimatedEndTime", render: fmtDateTime },
  { title: "By", dataIndex: "updatedByEmail", render: (v) => v || "—" },
];

const EMPTY = {
  enabled: false,
  title: "Under Maintenance",
  message:
    "We are currently performing scheduled maintenance. Please check back shortly.",
  estimatedEndTime: null,
};

const countWords = (str) => String(str || "").trim().split(/\s+/).filter(Boolean).length;

// Message can't exceed the word count of the default sample message above.
const MAX_MESSAGE_WORDS = countWords(EMPTY.message);

// <input type="datetime-local"> needs a local "YYYY-MM-DDTHH:mm" value, while
// the API stores ISO/UTC. Convert both ways so the picker round-trips cleanly.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const GlobalMaintenanceTab = () => {
  const [loading, setLoading] = useState(false);
  const [savingPlatform, setSavingPlatform] = useState(null);
  const [forms, setForms] = useState({
    android: { ...EMPTY },
    ios: { ...EMPTY },
  });
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await getMaintenanceHistory({ page, limit: 20 });
      if (res?.status) {
        setHistory(res.data?.rows || []);
        setHistoryTotal(res.data?.total || 0);
        setHistoryPage(res.data?.page || page);
      }
    } catch (e) {
      // History is non-critical — never block the page on its failure.
    } finally {
      setHistoryLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMaintenanceConfig();
      if (res?.status) {
        const data = res.data || {};
        setForms({
          android: { ...EMPTY, ...(data.android || {}) },
          ios: { ...EMPTY, ...(data.ios || {}) },
        });
      } else {
        toast.error(res?.message || "Load failed", { toastId: "maintenance-error" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Load failed", {
        toastId: "maintenance-error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHistory(1);
  }, []);

  const updateForm = (platform, patch) =>
    setForms((prev) => ({ ...prev, [platform]: { ...prev[platform], ...patch } }));

  const save = async (platform) => {
    if (savingPlatform) return; // guard against double-submit (rapid clicks)
    const form = forms[platform];
    // Snapshot exactly what we're sending — used below so a server response
    // that omits/nulls estimatedEndTime (e.g. a stale echo) never blanks out
    // the value the admin just set. A genuine clear (form value already null)
    // still round-trips to null, so this doesn't block clearing.
    const sentEstimatedEndTime = form.estimatedEndTime
      ? new Date(form.estimatedEndTime).toISOString()
      : null;
    setSavingPlatform(platform);
    try {
      const res = await upsertMaintenanceConfig({
        platform,
        enabled: !!form.enabled,
        title: form.title || "",
        message: form.message || "",
        estimatedEndTime: sentEstimatedEndTime,
      });
      if (res?.status) {
        toast.success(`${platform} maintenance settings saved`);
        // Reflect the canonical server value (e.g. normalized ETA), but never
        // let a missing estimatedEndTime in the response clear what we just sent.
        if (res.data) {
          updateForm(platform, {
            ...EMPTY,
            ...res.data,
            estimatedEndTime: res.data.estimatedEndTime ?? sentEstimatedEndTime,
          });
        }
        loadHistory(1); // surface the just-recorded audit entry
      } else {
        toast.error(res?.message || "Save failed", { toastId: "maintenance-error" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", {
        toastId: "maintenance-error",
      });
    } finally {
      setSavingPlatform(null);
    }
  };

  // NOTE: this is a plain render function, NOT a nested component rendered as
  // <PlatformCard/>. Defining a component inside another and mounting it as an
  // element remounts it on every parent render (each keystroke), which would
  // make the Title/Message inputs lose focus after every character. Calling a
  // function that returns JSX avoids that — it has no hooks of its own.
  const renderPlatformCard = (platform, label) => {
    const form = forms[platform];
    const saving = savingPlatform === platform;
    const isEnabling = !!form.enabled;

    const SaveButton = (
      <Button type="primary" danger={isEnabling} loading={saving}>
        Save {label}
      </Button>
    );

    return (
      <div
        key={platform}
        className="card"
        style={{ flex: "1 1 360px", minWidth: 320, padding: 16, background: "white" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h5 style={{ margin: 0 }}>
            {label}{" "}
            {form.enabled ? (
              <Tag color="red">MAINTENANCE ON</Tag>
            ) : (
              <Tag color="green">LIVE</Tag>
            )}
          </h5>
          <PermissionGate module="maintenance" action="update">
            {/* Confirm before enabling — turning this on locks out every
                user on this OS until it is turned back off. */}
            {isEnabling ? (
              <Popconfirm
                title={`Save maintenance settings for ${label}?`}
                description={`While maintenance is on, all ${label} app users are blocked until you turn it off.`}
                okText="Save"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                onConfirm={() => save(platform)}
              >
                {SaveButton}
              </Popconfirm>
            ) : (
              <span onClick={() => save(platform)}>{SaveButton}</span>
            )}
          </PermissionGate>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <label style={{ fontWeight: 600 }}>
              Maintenance mode
              <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 400 }}>
                When on, the {label} app shows a full-screen maintenance notice.
              </div>
            </label>
            <PermissionGate
              module="maintenance"
              action="update"
              fallback={<Switch checked={!!form.enabled} disabled />}
            >
              <Switch
                checked={!!form.enabled}
                onChange={(v) => updateForm(platform, { enabled: v })}
              />
            </PermissionGate>
          </div>

          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>Title</label>
            <Input
              value={form.title}
              maxLength={200}
              placeholder="Under Maintenance"
              onChange={(e) => updateForm(platform, { title: e.target.value })}
            />
          </div>

          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>Message</label>
            <Input.TextArea
              value={form.message}
              maxLength={1000}
              rows={3}
              placeholder="Shown to users on the maintenance screen"
              onChange={(e) => {
                const value = e.target.value;
                // Reject the keystroke rather than truncate mid-word — lets
                // deletes/edits through freely, only blocks growing past the limit.
                if (countWords(value) <= MAX_MESSAGE_WORDS) {
                  updateForm(platform, { message: value });
                }
              }}
            />
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
              {countWords(form.message)}/{MAX_MESSAGE_WORDS} words
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>
              Estimated end time (optional)
            </label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="datetime-local"
                value={toLocalInput(form.estimatedEndTime)}
                onChange={(e) =>
                  updateForm(platform, {
                    estimatedEndTime: e.target.value || null,
                  })
                }
                style={{ padding: "6px 8px", border: "1px solid #d9d9d9", borderRadius: 6 }}
              />
              {form.estimatedEndTime && (
                <Button
                  size="small"
                  onClick={() => updateForm(platform, { estimatedEndTime: null })}
                >
                  Clear
                </Button>
              )}
            </div>
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
              Displayed on the app's maintenance screen. Leave blank if unknown.
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <p style={{ opacity: 0.7, marginBottom: 16 }}>
        Toggle maintenance independently for Android and iOS. When enabled, the
        corresponding mobile app immediately shows a full-screen maintenance
        notice and blocks all activity until you turn it off.
      </p>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16, maxWidth: 750 }}
        message="Enabling maintenance locks out every user on that platform. Remember to turn it back off when you're done."
      />

      {loading ? (
        <Spin />
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 24,
            maxWidth: 1100,
            alignItems: "stretch",
          }}
        >
          {PLATFORMS.map((p) => renderPlatformCard(p.key, p.label))}
        </div>
      )}

      {/* ---- HISTORY / AUDIT TRAIL ---- */}
      <div
        className="card"
        style={{ padding: 16, background: "white", maxWidth: 980, marginTop: 8 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h5 style={{ margin: 0 }}>History</h5>
          <Button
            size="small"
            onClick={() => loadHistory(historyPage)}
            loading={historyLoading}
          >
            Refresh
          </Button>
        </div>
        <Table
          rowKey={(r) => r._id}
          size="small"
          loading={historyLoading}
          columns={HISTORY_COLUMNS}
          dataSource={history}
          pagination={{
            current: historyPage,
            pageSize: 20,
            total: historyTotal,
            showSizeChanger: false,
            onChange: (p) => loadHistory(p),
          }}
        />
      </div>
    </>
  );
};

export default GlobalMaintenanceTab;
